import { guid } from '../../../../utils/guid';
import { BomTreeItem } from './bom-tree-item.model';

export interface BomTreeItemVm {
  id: string; // guid or other unique temp id
  // model: BomTreeItem;
  data: BomTreeItemData;
  children: BomTreeItemVm[];

  /**
   * Alternatives / ideas:
   * - simplify to single flag 'loading'
   * - add global flag state.loading
   * - ignore loading/hydrating flags completely and add array state.loadingIds[] as a hybrid approach ???
   */
  hydrating: boolean;
}

export type BomTreeItemData = Omit<BomTreeItem, 'children'>;

// maybe change this from mapDtoToViewModel ???
export function mapModelToViewModel(model: BomTreeItem): BomTreeItemVm {
  const { children, ...data } = model;

  const vm: BomTreeItemVm = {
    id: guid(),
    // model, // TODO: drop children array from model here?
    data,
    children: model.children.map(mapModelToViewModel),
    hydrating: false,
  };

  return vm;
}

export function updateTree(root: BomTreeItemVm, nodeId: string, update: (node: BomTreeItemVm) => BomTreeItemVm): BomTreeItemVm {
  if (root.id === nodeId) {
    return update(root);
  }

  return {
    ...root,
    children: root.children.map(child => updateTree(child, nodeId, update))
  };
}

export function updateNode(nodeId: string, update: (node: BomTreeItemVm) => BomTreeItemVm): (root: BomTreeItemVm) => BomTreeItemVm {
  return root => {
    if (root.id === nodeId) {
      return update(root);
    }

    return {
      ...root,
      children: root.children.map(updateNode(nodeId, update))
    };
  };
}

export function addChild(nodeId: string, newChild: BomTreeItemVm): (root: BomTreeItemVm) => BomTreeItemVm {
  return root => {
    if (root.id === nodeId) {
      return {
        ...root,
        children: [...root.children, newChild]
      };
    }

    return {
      ...root,
      children: root.children.map(addChild(nodeId, newChild))
    };
  };
}

// updates EVERY matching node — the same BOM item can appear at several places in the tree
export function updateNodesByBomItemId(bomItemId: number, update: (node: BomTreeItemVm) => BomTreeItemVm): (root: BomTreeItemVm) => BomTreeItemVm {
  return root => {
    const updated = root.data.bomItemId === bomItemId ? update(root) : root;

    return {
      ...updated,
      children: updated.children.map(updateNodesByBomItemId(bomItemId, update))
    };
  };
}

export function replaceNode(nodeId: string, newNode: BomTreeItemVm): (root: BomTreeItemVm) => BomTreeItemVm {
  return root => {
    if (root.id === nodeId) {
      return newNode;
    }

    return {
      ...root,
      children: root.children.map(replaceNode(nodeId, newNode))
    };
  };
}

export function removeNode(nodeId: string): (root: BomTreeItemVm) => BomTreeItemVm {
  return root => {
    const newChildren = root.children.filter(child => child.id !== nodeId);

    if (root.children.length !== newChildren.length) {
      return {
        ...root,
        children: newChildren
      };
    }

    return {
      ...root,
      children: root.children.map(removeNode(nodeId))
    };
  };
}

export interface NodeWithParent {
  node?: BomTreeItemVm;
  parent?: BomTreeItemVm;
}

export namespace BomTreeVm {
  export function* traverse(root: BomTreeItemVm): IterableIterator<BomTreeItemVm> {
    yield root;

    for (const child of root.children) {
      yield* traverse(child);
    }
  }

  export function findNodeById(root: BomTreeItemVm, nodeId: string): BomTreeItemVm | undefined {
    for (const node of traverse(root)) {
      if (node.id === nodeId) {
        return node;
      }
    }
    return undefined;
  }

  export function getNodeById(root: BomTreeItemVm, nodeId: string): BomTreeItemVm {
    const node = findNodeById(root, nodeId);

    if (!node) {
      throw new Error(`Node ${nodeId} not found`);
    }

    return node;
  }

  export function getParentNodeById(root: BomTreeItemVm, nodeId: string): BomTreeItemVm | undefined {
    // if (root.children.some(child => child.id === nodeId)) {
    //   return root;
    // }

    // const toVisit: BomTreeItemVm = [];

    for (const child of root.children) {
      if (child.id === nodeId) {
        return root;
      }

      const parent = getParentNodeById(child, nodeId);

      if (parent) {
        return parent;
      }
    }

    return undefined;
  }

  export function getNodeWithParentById(root: BomTreeItemVm, nodeId: string): NodeWithParent {
    if (root.id === nodeId) {
      return {
        node: root,
        parent: undefined
      };
    }

    for (const child of root.children) {
      if (child.id === nodeId) {
        return {
          node: child,
          parent: root
        };
      }

      const nodeWithParent = getNodeWithParentById(child, nodeId);

      if (nodeWithParent.node) {
        return nodeWithParent;
      }
    }

    return { node: undefined, parent: undefined };
  }

  export function removeNode(root: BomTreeItemVm, nodeId: string): BomTreeItemVm {
    const newChildren = root.children.filter(child => child.id !== nodeId);

    if (root.children.length !== newChildren.length) {
      return {
        ...root,
        children: newChildren
      };
    }

    return {
      ...root,
      children: root.children.map(child => removeNode(child, nodeId))
    };
  }
}
